import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/luckySpin.css';
import SpinBackground from '../components/SpinBackground';
import HorizontalSlotReel from '../components/HorizontalSlotReel';
import FreeTicketTimer from '../../tickets/components/FreeTicketTimer';
import { useTicketCollection } from '../../tickets/hooks/useTicketCollection';
import { useUserRedux } from '../../../hooks/useUserRedux';
import Popover from '../../../shared/components/Popover';
import InviteFriendsModal from '../../social/pages/InviteFriendsModal';
import TicketCollectorModal from '../../../pages/TicketCollectorModal';
import TicketConverterModal from '../../tickets/components/TicketConverterModal';
import BoostModal from '../components/BoostModal';
import AutoCollectModal from '../components/AutoCollectModal';
import DatumSpinModal from '../components/DatumSpinModal';
import DatumPassOfferModal from '../../datumPass/components/DatumPassOfferModal';
import { fetchDatumPassStatus } from '../../datumPass/store/datumPassSlice';
import { fetchBattlePass } from '../../battlePass/store/battlePassSlice';
import crownImg from '../../../images/datumpass/bp_crown.png';
import { luckySpinAPI, shopAPI, adRewardsAPI } from '../../../services/api';
import { showAdAndGrant } from '../../../shared/utils/adsgram';
import { useRewards } from '../hooks/useRewardsRedux';
import { useProgress } from '../hooks/useProgressRedux';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { entityText } from '../../../i18n/entityText';
import { rewardLabel } from '../../../shared/utils/gameLabels';
import { enqueueReward } from '../store/rewardsQueueSlice';
import { fetchTicketStatus, setAutoCollectEnabled } from '../../tickets/store/ticketsSlice';
import { ticketsAPI } from '../../../services/api';
import { fetchUser } from '../../../store/slices/userSlice';
import { fetchShopBoosts, fetchAutoCollectItems } from '../../shop/store/shopSlice';
import {
  fetchWalletBalance,
  updateWalletAfterSpin,
  decrementTicket,
  decrementBoostTicket,
  incrementTicket,
  incrementBoostTicket,
} from '../../wallet/store/walletSlice';
import { fetchProgress, claimProgressReward, selectDisplayedStageReward, updateFromWebSocket as updateProgress } from '../store/progressSlice';
import { fetchNotifications } from '../../../store/slices/countersSlice';

import { formatStageReward } from '../utils/stageReward';
import { fetchRewards } from '../store/rewardsSlice';
import { useLinkedWallet } from '../../wallet/hooks/useLinkedWallet';
import { tutorialEvent } from '../../tutorial/store/tutorialSlice';

// ── Datum Spin offer: once per app session ──────────────────────────────────
// A useRef resets every time LuckySpinPage remounts (switching bottom-menu tabs),
// so the flag lives at module level (survives tab navigation) and is mirrored to
// sessionStorage (survives a WebView reload within the same Telegram session).
const DATUM_SPIN_OFFER_KEY = 'datumSpinOfferShown';
let datumSpinOfferShownInSession = false;

const wasDatumSpinOfferShown = () => {
  if (datumSpinOfferShownInSession) return true;
  try {
    return sessionStorage.getItem(DATUM_SPIN_OFFER_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const markDatumSpinOfferShown = () => {
  datumSpinOfferShownInSession = true;
  try {
    sessionStorage.setItem(DATUM_SPIN_OFFER_KEY, '1');
  } catch (_) {
    // storage unavailable — the module-level flag still covers tab switches
  }
};

const MOCK_REWARDS = [
  { label: 'Head',   icon: '/slider/head.png',      count: 'x1',   raw: { type: 'MODULE',  subtype: 'HEAD',   value: 1   } },
  { label: 'Body',   icon: '/slider/torso.png',     count: 'x1',   raw: { type: 'MODULE',  subtype: 'BODY',   value: 1   } },
  { label: 'Hand',   icon: '/slider/hand_left.png', count: 'x1',   raw: { type: 'MODULE',  subtype: 'HAND',   value: 1   } },
  { label: 'Leg',    icon: '/slider/leg.png',        count: 'x1',   raw: { type: 'MODULE',  subtype: 'LEG',    value: 1   } },
  { label: 'Coins',  icon: '/slider/coins_1.png',   count: 'x500', raw: { type: 'COINS',   subtype: 'COINS',  value: 500 } },
  { label: 'GRAM',   icon: '/slider/ton.png',        count: 'x0.1', raw: { type: 'TON',     subtype: 'TON',    value: 0.1 } },
  { label: 'Ticket', icon: '/ticket_icon.png',       count: 'x1',   raw: { type: 'TICKETS', subtype: 'TICKET', value: 1   } },
];

export default function LuckySpin() {
  const { t }        = useTranslation();
  const dispatch     = useDispatch();
  const navigate     = useNavigate();
  const slotRef      = useRef();

  // ── Spin guards ───────────────────────────────────────────────────────────
  const spinInFlightRef     = useRef(false);
  const finalSpinIndexRef   = useRef(null);
  const rewardEnqueuedRef   = useRef(false);
  const backendRewardDataRef = useRef(null);

  // ── AutoSpin state ────────────────────────────────────────────────────────
  const autoSpinActiveRef    = useRef(false);
  const collectedRewardsRef  = useRef([]);
  const autospinStartedAtRef = useRef(0);

  // ── Stopping state: last spin is finishing after STOP pressed ─────────────
  // stopRequestedRef: true means STOP was pressed while a spin was in flight.
  // finalizeStopAutoSpin() will be called from onSpinEnd when the reel settles.
  const stopRequestedRef          = useRef(false);
  const nextSpinTimerRef          = useRef(null); // timer between autospins — cancellable
  const finalizeStopAutoSpinRef   = useRef(null); // always points to latest finalizeStopAutoSpin

  // ── Hold-to-autospin ──────────────────────────────────────────────────────
  const holdTimerRef          = useRef(null);
  const isHoldRef             = useRef(false);
  const pressStartRef         = useRef(null);
  const hapticTimersRef       = useRef([]);
  const handleSpinRef         = useRef(null); // always points to latest handleSpin
  const autoSpinFirstHapticRef = useRef(false);
  const HOLD_MS               = 1000;

  // Mirrors spinning state — lets press handlers read fresh value without stale closure
  const spinningRef = useRef(false);

  const spinBgRef = useRef(null);

  const { rewards }  = useRewards();
  const progress     = useProgress();
  const { isAvailable, handleClaim } = useTicketCollection();
  const { data: user, refreshUser }  = useUserRedux();

  const { linkedWallet, isWalletLinked, connect } = useLinkedWallet();

  const [tonConnecting, setTonConnecting]     = useState(false);
  const [monitorReady, setMonitorReady]       = useState(false);
  const [spinning, setSpinning]               = useState(false);
  const [autoSpinMode, setAutoSpinMode]       = useState(false);
  const [isClaiming, setIsClaiming]           = useState(false);
  const [spinError, setSpinError]             = useState(null);
  const [backendRewardData, setBackendRewardData] = useState(null);
  const [showPopover, setShowPopover]         = useState(false);
  const [showInviteFriend, setShowInviteFriend]   = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showConverter, setShowConverter]     = useState(false);
  const [showBoostModal, setShowBoostModal]   = useState(false);
  const [boostTicketItem, setBoostTicketItem] = useState(null);
  const [showAutoCollectModal, setShowAutoCollectModal] = useState(false);
  const [showDatumPassModal, setShowDatumPassModal] = useState(false);
  const [showDatumSpinModal, setShowDatumSpinModal] = useState(false);
  // AdsGram вернул «No available ad» (code 403) — прячем рекламную кнопку и
  // показываем обычный "Please wait", чтобы не блокировать игрока
  const [adUnavailable, setAdUnavailable] = useState(false);
  // Доступность тикета за рекламу: общий кулдаун с ячейкой "Watch Ad" в Shop
  // (app_settings.AD_TICKET_COOLDOWN_MINUTES), независим от таймера бесплатного тикета
  const [adTicketStatus, setAdTicketStatus] = useState(null);
  const [collectedRewards, setCollectedRewards] = useState([]);
  // true while autoSpin was stopped but the last reel animation hasn't finished yet
  const [stoppingAutoSpin, setStoppingAutoSpin] = useState(false);

  const totalTicketsClaimed  = useSelector(state => state.wallet.tickets);
  const datumCoins           = useSelector(state => state.wallet.datumCoins);
  const boostTickets         = useSelector(state => state.wallet.boostTickets);
  const boostEnabled         = boostTickets > 0;
  const isAutoCollectEnabled = useSelector(state => state.tickets.isAutoCollectEnabled);
  const isDatumPassEnabled   = useSelector(state => state.datumPass.isEnabled);
  const datumPassExpiresAt   = useSelector(state => state.datumPass.expiresAt);
  const bpSeason             = useSelector(state => state.battlePass.season);
  const bpTiers              = useSelector(state => state.battlePass.tiers);
  const bpHasPass            = useSelector(state => state.battlePass.userProgress?.hasDatumPass);
  const bpBadgeFromCounters  = useSelector(state => state.counters.battlePass);
  // Claimable tiers derived from the BattlePass payload itself (freeStatus / premiumStatus === 'AVAILABLE').
  // Combined with the notifications counter so the badge shows even if one of the sources is stale.
  const bpBadgeFromTiers = useMemo(() => (bpTiers || []).reduce((n, tier) =>
    n + (tier.freeStatus === 'AVAILABLE' ? 1 : 0)
      + ((bpHasPass || isDatumPassEnabled) && tier.premiumStatus === 'AVAILABLE' ? 1 : 0), 0),
  [bpTiers, bpHasPass, isDatumPassEnabled]);
  const bpUnclaimedCount     = Math.max(bpBadgeFromCounters || 0, bpBadgeFromTiers);
  const pendingTickets       = useSelector(state => state.tickets.pendingTickets ?? 1);
  const shopBoosts           = useSelector(state => state.shop.boosts);
  const autoCollectItems     = useSelector(state => state.shop.autoCollectItems);

  // ── Helper: sync spinning state and ref together ──────────────────────────
  const setSpin = useCallback((val) => {
    spinningRef.current = val;
    setSpinning(val);
  }, []);

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchRewards('base'));
    dispatch(fetchProgress());
    dispatch(fetchShopBoosts());
    dispatch(fetchAutoCollectItems());
    dispatch(fetchWalletBalance());
    dispatch(fetchDatumPassStatus());
    dispatch(fetchBattlePass());
    shopAPI.getBoostTicketItem()
      .then(item => setBoostTicketItem(item))
      .catch(() => {});
  }, [dispatch]);

  // ── Auto-stop autospin when tickets run out ───────────────────────────────
  useEffect(() => {
    if (autoSpinMode && totalTicketsClaimed === 0 && boostTickets === 0) {
      handleStop();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalTicketsClaimed, boostTickets, autoSpinMode]);

  // ── Restore smile when tickets hit 0 after a spin ────────────────────────
  useEffect(() => {
    if (totalTicketsClaimed === 0 && boostTickets === 0 && !spinning && !autoSpinMode && monitorReady) {
      setMonitorReady(false);
      // Wait for smileVisible effect in SpinBackground to run, then play the animation
      setTimeout(() => {
        spinBgRef.current?.playNoTickets();
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalTicketsClaimed, boostTickets, spinning, autoSpinMode]);

  // ── Datum Spin offer: last ticket left while still early in the game ─────
  // Shows once per session when the player is down to 1 ticket (no boost)
  // and has made no more than 50 total spins. Waits for the reel to settle.
  useEffect(() => {
    if (
      !wasDatumSpinOfferShown() &&
      !spinning && !autoSpinMode && !stoppingAutoSpin &&
      totalTicketsClaimed === 1 && boostTickets === 0 &&
      typeof progress?.totalSpins === 'number' && progress.totalSpins <= 50
    ) {
      markDatumSpinOfferShown();
      setShowDatumSpinModal(true);
    }
  }, [totalTicketsClaimed, boostTickets, spinning, autoSpinMode, stoppingAutoSpin, progress?.totalSpins]);

  // ── Retry ads after "No available ad" ─────────────────────────────────────
  // Инвентарь AdsGram пополняется со временем — через 5 минут снова предлагаем рекламу
  useEffect(() => {
    if (!adUnavailable) return;
    const timer = setTimeout(() => setAdUnavailable(false), 5 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [adUnavailable]);

  // ── Ad-ticket availability (shared cooldown with the Shop "Watch Ad" cell) ─
  const refreshAdTicketStatus = useCallback(() => {
    adRewardsAPI.getFreeTicketStatus().then(setAdTicketStatus).catch(() => {});
  }, []);

  useEffect(() => { refreshAdTicketStatus(); }, [refreshAdTicketStatus]);

  // Когда кулдаун истекает — перечитываем статус, чтобы кнопка рекламы вернулась сама
  useEffect(() => {
    if (!adTicketStatus || adTicketStatus.isAvailable || !adTicketStatus.nextAvailableTime) return;
    const delay = new Date(adTicketStatus.nextAvailableTime).getTime() - Date.now() + 1000;
    const timer = setTimeout(refreshAdTicketStatus, Math.max(delay, 1000));
    return () => clearTimeout(timer);
  }, [adTicketStatus, refreshAdTicketStatus]);

  // Секундный тикер для обратного отсчёта на рекламной кнопке (пока идёт кулдаун)
  const [, setAdTick] = useState(0);
  useEffect(() => {
    if (!adTicketStatus || adTicketStatus.isAvailable || !adTicketStatus.nextAvailableTime) return;
    const timer = setInterval(() => setAdTick(n => n + 1), 1000);
    return () => clearInterval(timer);
  }, [adTicketStatus]);

  // ── Reward helpers ────────────────────────────────────────────────────────
  const getModuleImage = (rewardData, backendData) => {
    if (rewardData?.raw?.type === 'MODULE') {
      if (backendData?.module?.urlSmall) return backendData.module.urlSmall;
      if (backendData?.imageUrl)         return backendData.imageUrl;
      return rewardData?.icon || '/slider/eagle_head.png';
    }
    return rewardData?.icon || '/slider/coins_1.png';
  };

  const mapAnimation = (r) => {
    if (r.raw?.type === 'MODULE') return 'inventory';
    if (r.raw?.type === 'CHEST')  return 'inventory';
    if (r.raw?.type === 'KEY')    return 'inventory';
    if (r.raw?.type === 'LIVES')  return 'lives';
    if (r.label === 'Ticket')     return 'tickets';
    if (r.label === 'GRAM')       return 'ton';
    if (r.label === 'Coins')      return 'coins';
    return 'inventory';
  };

  const mapSound = (r) => {
    if (r.label === 'Ticket') return 'gotTicket';
    if (r.label === 'GRAM')   return 'gotTon';
    if (r.label === 'Coins')  return 'gotCoins';
    return 'reward';
  };

  // ── Transformed rewards ───────────────────────────────────────────────────
  const transformedRewards = useMemo(() => {
    if (!Array.isArray(rewards) || rewards.length === 0) return [];
    const unique = rewards.filter((item, idx, arr) =>
      arr.findIndex(o => o.subtype === item.subtype) === idx
    );
    const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
    return unique.map(item => {
      let label = '';
      let icon  = item.imageUrl || '/slider/coins_1.png';
      switch (item.type) {
        case 'COINS':   label = 'Coins';  icon = item.imageUrl || '/slider/coins_1.png'; break;
        case 'TICKETS': label = 'Ticket'; icon = item.imageUrl || '/ticket_icon.png';    break;
        case 'TON':     label = 'GRAM';   icon = item.imageUrl || '/slider/ton.png';      break;
        case 'MODULE':
          switch (item.moduleType) {
            case 'HEAD': label = 'Head'; break;
            case 'BODY': label = 'Body'; break;
            case 'HAND': label = 'Hand'; break;
            case 'LEG':  label = 'Leg';  break;
            default:     label = 'Module';
          }
          break;
        case 'CHEST':
          label = item.chestRarity ? `${cap(item.chestRarity)} Chest` : 'Chest';
          icon  = item.chestImageUrl || icon;
          break;
        case 'KEY':
          label = item.keyRarity ? `${cap(item.keyRarity)} Key` : 'Key';
          icon  = item.keyImageUrl || icon;
          break;
        // v1.51.0: lives of mini-games
        case 'LIVES': label = 'Lives'; icon = item.imageUrl || '/lair/heart.svg'; break;
        default: label = item.type;
      }
      return {
        label,
        icon,
        count: item.value >= 1 ? `x${item.value}` : `+${item.value}`,
        raw: { ...item, subtype: item.subtype },
      };
    });
  }, [rewards]);

  // label — идентификатор (mapAnimation/mapSound), на ленте показываем перевод (displayLabel)
  const effectiveRewards = useMemo(
    () => (transformedRewards.length > 0 ? transformedRewards : MOCK_REWARDS).map((r) => ({
      ...r,
      displayLabel: rewardLabel(t, {
        type: r.raw.type, label: r.label, moduleType: r.raw.moduleType,
        chestRarity: r.raw.chestRarity, keyRarity: r.raw.keyRarity,
      }),
    })),
    [transformedRewards, t],
  );

  // ── Claim free ticket ─────────────────────────────────────────────────────
  const handleClaimOnly = async () => {
    if (isClaiming) return;
    try {
      setIsClaiming(true);
      const result = await handleClaim();
      const earned = result?.ticketsEarned ?? 1;
      dispatch(fetchUser());
      dispatch(fetchWalletBalance());
      dispatch(fetchTicketStatus());
      dispatch(fetchNotifications());
      setTimeout(() => {
        dispatch(enqueueReward({
          type: 'TICKETS', value: earned, source: 'tickets',
          icon: '/ticket_icon.png', label: 'Ticket',
          animationTarget: 'tickets', sound: 'gotTicket',
        }));
      }, 500);
    } catch (err) {
      console.error('Failed to claim ticket:', err);
    } finally {
      setIsClaiming(false);
    }
  };

  // ── Дополнительный тикет за просмотр рекламы (AdsGram) ────────────────────
  // Механика подтверждения та же, что в Shop: initiate → показ рекламы →
  // webhook + поллинг статуса. Тикет начисляет бэкенд (grantAdTicket) в обход
  // таймера бесплатного тикета; частоту ограничивает общий с Shop кулдаун.
  const handleWatchAdv = async () => {
    if (isClaiming) return;
    try {
      setIsClaiming(true);
      const granted = await showAdAndGrant('FREE_TICKET', {
        onNoAd: () => setAdUnavailable(true),
      });
      if (!granted) return;
      dispatch(fetchUser());
      dispatch(fetchWalletBalance());
      dispatch(fetchTicketStatus());
      dispatch(fetchNotifications());
      setTimeout(() => {
        dispatch(enqueueReward({
          type: 'TICKETS', value: 1, source: 'tickets',
          icon: '/ticket_icon.png', label: 'Ticket',
          animationTarget: 'tickets', sound: 'gotTicket',
        }));
      }, 500);
    } catch (err) {
      console.error('Failed to get free ticket for watching ad:', err);
    } finally {
      setIsClaiming(false);
      refreshAdTicketStatus();
    }
  };

  // ── Spin ──────────────────────────────────────────────────────────────────
  const handleSpin = async () => {
    if (spinInFlightRef.current || isClaiming) return;
    if (!slotRef.current) return;
    if (isAvailable && !autoSpinActiveRef.current) return handleClaimOnly();

    spinInFlightRef.current    = true;
    rewardEnqueuedRef.current  = false;
    backendRewardDataRef.current = null;
    finalSpinIndexRef.current  = null;

    setMonitorReady(true);
    setSpin(true);
    setSpinError(null);
    setBackendRewardData(null);
    if (!autoSpinActiveRef.current) setCollectedRewards([]);

    // Optimistic decrement — обновляем баланс tickets в state сразу, без запроса к API.
    // Работает и в одиночном спине, и в autospin: пользователь видит, как падает счётчик
    // на каждой прокрутке. Реальная синхронизация с бэкендом — через fetchWalletBalance:
    //   • single spin → после показа модалки награды (onSpinEnd)
    //   • autospin    → один раз в finalizeStopAutoSpin после STOP
    // Если спин не состоялся (429 / ошибка) — откатываем декремент в catch ниже, чтобы
    // повторные попытки и неудачные спины не «съедали» баланс лишний раз.
    let decrementedKind = null; // 'boost' | 'ticket' | null — нужно для отката
    if (boostTickets > 0) {
      dispatch(decrementBoostTicket());
      decrementedKind = 'boost';
    } else if (totalTicketsClaimed > 0) {
      dispatch(decrementTicket());
      decrementedKind = 'ticket';
    }

    slotRef.current.startSpin();
    dispatch(tutorialEvent('spin'));

    // Виброотклик "тррррынь" при старте анимации:
    // - одиночный спин: каждый раз
    // - autospin: только при первом спине
    if (!autoSpinActiveRef.current) {
      triggerSpinStartHaptic();
    } else if (!autoSpinFirstHapticRef.current) {
      autoSpinFirstHapticRef.current = true;
      triggerSpinStartHaptic();
    }

    try {
      const result = await luckySpinAPI.spin(null);

      const rewardIndex  = effectiveRewards.findIndex(r => r.raw.subtype === result.reward.subtype);
      const landingIndex = rewardIndex >= 0 ? rewardIndex : 0;

      finalSpinIndexRef.current    = landingIndex;
      backendRewardDataRef.current = result.reward;
      setBackendRewardData(result.reward);

      // Update lucky spin tickets and boost tickets immediately (counters/bars should react right away).
      // Full balance (coins/tickets) is intentionally deferred until after the reward modal:
      // - single spin: fetchWalletBalance fires after reward modal shows (onSpinEnd)
      // - autospin: balance stays frozen; updates after STOP via RewardCenter confirm
      if (result.userBalance) dispatch(updateWalletAfterSpin(result.userBalance));
      if (result.progress) dispatch(updateProgress(result.progress));

      slotRef.current.stopAtItem(landingIndex);

    } catch (err) {
      console.error('Spin failed:', err);

      const isCooldown = err?.response?.status === 429;

      if (isCooldown) {
        // Backend spin cooldown (parallel tab / very fast autospin).
        // In autospin mode: abort current animation and retry after the cooldown.
        // In single-spin mode: show a brief banner.
        const retryAfterMs = parseInt(err?.response?.headers?.['retry-after-ms'] || '1500', 10);

        // Спин не состоялся → откатываем оптимистический декремент тикета
        if (decrementedKind === 'boost') dispatch(incrementBoostTicket());
        else if (decrementedKind === 'ticket') dispatch(incrementTicket());

        finalSpinIndexRef.current    = null;
        backendRewardDataRef.current = null;
        setBackendRewardData(null);
        slotRef.current?.abort();
        spinInFlightRef.current = false;
        setSpin(false);

        if (stopRequestedRef.current) {
          // STOP was pressed while this spin was in flight — abort() won't call
          // onSpinEnd, so finalizeStopAutoSpin would never run. Finalize now.
          finalizeStopAutoSpinRef.current?.();
        } else if (autoSpinActiveRef.current) {
          // Quietly retry after the cooldown — don't interrupt autospin
          setTimeout(() => {
            rewardEnqueuedRef.current = false;
            handleSpinRef.current?.();
          }, retryAfterMs + 100);
        } else {
          setSpinError(t('spin.error.cooldown', 'Please wait a moment before spinning again.'));
        }
        return;
      }

      const isNetworkError = !err.response;
      const errorMsg = isNetworkError
        ? t('spin.error.connection', 'Connection lost. Please check your network.')
        : (err?.response?.data?.message || err?.response?.data?.error || t('spin.error.failed', 'Spin failed. Please try again.'));

      // Спин не состоялся → откатываем оптимистический декремент тикета
      if (decrementedKind === 'boost') dispatch(incrementBoostTicket());
      else if (decrementedKind === 'ticket') dispatch(incrementTicket());

      finalSpinIndexRef.current    = null;
      backendRewardDataRef.current = null;
      setBackendRewardData(null);
      slotRef.current?.abort();
      spinInFlightRef.current = false;
      setSpinError(errorMsg);

      if (stopRequestedRef.current) {
        // STOP was pressed while this spin was in flight — finalize so UI doesn't hang.
        finalizeStopAutoSpinRef.current?.();
      } else if (autoSpinActiveRef.current) {
        // Stop autospin on any non-cooldown error — no silent retry
        autoSpinActiveRef.current  = false;
        setAutoSpinMode(false);
        collectedRewardsRef.current = [];
        setCollectedRewards([]);
        setSpin(false);
      } else {
        setSpin(false);
      }
    }
  };

  // Keep ref current so hold timer / autospin loop always call the latest closure
  handleSpinRef.current = handleSpin;

  // ── Finalize autospin after the last reel animation completes ────────────
  // Called either immediately (if no spin was in flight when STOP pressed)
  // or from onSpinEnd after the reel settles.
  const finalizeStopAutoSpin = useCallback(() => {
    stopRequestedRef.current = false;
    setStoppingAutoSpin(false);
    setSpin(false);
    spinInFlightRef.current   = false;
    rewardEnqueuedRef.current = false;
    finalSpinIndexRef.current = null;
    backendRewardDataRef.current = null;
    setBackendRewardData(null);

    const accumulated = [...collectedRewardsRef.current];
    collectedRewardsRef.current = [];
    setCollectedRewards([]);

    // Merge same-type rewards and show modals sequentially
    const grouped = [];
    for (const reward of accumulated) {
      const key      = reward.type + '_' + reward.label;
      const existing = grouped.find(r => r.type + '_' + r.label === key);
      if (existing) {
        existing.value = (Number(existing.value) || 0) + (Number(reward.value) || 0);
      } else {
        grouped.push({ ...reward });
      }
    }
    grouped.forEach((reward, i) => {
      setTimeout(() => dispatch(enqueueReward(reward)), i * 1800);
    });

    dispatch(fetchWalletBalance());
    // Spins unlock BattlePass tiers / achievements — refresh badges (BP pill, Tasks)
    dispatch(fetchNotifications());
    dispatch(fetchBattlePass());
  }, [dispatch, setSpin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ref current so timers/onSpinEnd always call the latest closure
  finalizeStopAutoSpinRef.current = finalizeStopAutoSpin;

  // ── Stop AutoSpin ─────────────────────────────────────────────────────────
  const handleStop = useCallback(() => {
    // Ignore accidental tap that fires immediately when STOP button appears
    if (Date.now() - autospinStartedAtRef.current < 500) return;
    // Guard re-entrancy: already processing a stop
    if (stopRequestedRef.current) return;

    autoSpinActiveRef.current = false;
    stopRequestedRef.current  = true;
    setAutoSpinMode(false);

    // Cancel any queued inter-spin timer so no new spin launches
    if (nextSpinTimerRef.current) {
      clearTimeout(nextSpinTimerRef.current);
      nextSpinTimerRef.current = null;
    }

    if (!spinInFlightRef.current) {
      // No spin in flight right now — finalize immediately, no waiting needed
      finalizeStopAutoSpinRef.current?.();
    } else {
      // A spin is in flight: the reel is still animating.
      // Show "finishing" UI and wait for onSpinEnd to call finalizeStopAutoSpin.
      setStoppingAutoSpin(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Haptic helpers ────────────────────────────────────────────────────────
  const triggerSpinStartHaptic = () => {
    const hf = window.Telegram?.WebApp?.HapticFeedback;
    if (!hf) return;
    // Ratchet: dense taps at start (wheel accelerating), gradually spacing out
    // over ~1.5 s to match the reel spin-up animation
    const beats = [
      [0,    'soft'],
      [80,   'light'],
      [150,  'light'],
      [220,  'medium'],
      [300,  'medium'],
      [390,  'medium'],
      [490,  'heavy'],
      [600,  'heavy'],
      [720,  'rigid'],
      [850,  'heavy'],
      [990,  'medium'],
      [1140, 'medium'],
      [1300, 'light'],
      [1470, 'soft'],
    ];
    beats.forEach(([delay, style]) =>
      setTimeout(() => hf.impactOccurred(style), delay)
    );
  };

  const startHapticSequence = () => {
    const hf = window.Telegram?.WebApp?.HapticFeedback;
    if (!hf) return;
    const steps  = [0, 200, 420, 660, 880];
    const styles = ['soft', 'light', 'medium', 'heavy', 'rigid'];
    hapticTimersRef.current = steps.map((delay, i) =>
      setTimeout(() => hf.impactOccurred(styles[i]), delay)
    );
  };

  const clearHapticSequence = () => {
    hapticTimersRef.current.forEach(clearTimeout);
    hapticTimersRef.current = [];
  };

  const startAutospin = () => {
    clearHapticSequence();
    pressStartRef.current = null; // clear hold state so released pointer can't retrigger anything
    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    autospinStartedAtRef.current  = Date.now();
    autoSpinActiveRef.current     = true;
    autoSpinFirstHapticRef.current = false;
    setAutoSpinMode(true);
    collectedRewardsRef.current  = [];
    setCollectedRewards([]);
    setMonitorReady(true);
    handleSpinRef.current?.();
  };

  // ── Press handlers — no stale-closure risk (all reads go through refs) ────
  const handlePressStart = useCallback(() => {
    if (spinningRef.current || autoSpinActiveRef.current || stopRequestedRef.current) return;
    pressStartRef.current = Date.now();
    isHoldRef.current     = false;
    startHapticSequence();
    holdTimerRef.current = setTimeout(() => {
      isHoldRef.current    = true;
      holdTimerRef.current = null;
      startAutospin();
    }, HOLD_MS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePressEnd = useCallback(() => {
    if (pressStartRef.current == null) return;
    const elapsed         = Date.now() - pressStartRef.current;
    pressStartRef.current = null;
    clearHapticSequence();

    // Autospin already active — only STOP button stops it
    if (autoSpinActiveRef.current) {
      isHoldRef.current = false;
      return;
    }

    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    isHoldRef.current = false;

    // Race-condition fallback: pointerUp raced ahead of the timer
    if (elapsed >= HOLD_MS) {
      startAutospin();
      return;
    }

    // Short press → single spin
    handleSpinRef.current?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePressCancel = useCallback(() => {
    clearHapticSequence();
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    pressStartRef.current = null;
    isHoldRef.current     = false;
  }, []);

  // ── SpinBackground cycle complete ─────────────────────────────────────────
  const handleAnimationCycleComplete = useCallback(() => {}, []);

  // ── Auto-collect toggle ───────────────────────────────────────────────────
  const handleAutoCollectToggle = async () => {
    const next = !isAutoCollectEnabled;
    dispatch(setAutoCollectEnabled(next));
    try {
      await ticketsAPI.setAutoCollect(next);
    } catch (err) {
      console.error('Failed to set auto-collect:', err);
      dispatch(setAutoCollectEnabled(!next));
    }
  };

  // ── TON Wallet connect ────────────────────────────────────────────────────
  const handleConnectTon = async () => {
    if (tonConnecting || isWalletLinked) return;
    try {
      setTonConnecting(true);
      await connect();
    } catch (err) {
      console.error('TON wallet connection failed:', err);
    } finally {
      setTonConnecting(false);
    }
  };

  const tonButtonLabel = (() => {
    if (tonConnecting) return t('wallet.connecting', 'Connecting...');
    if (isWalletLinked) {
      const short = linkedWallet.slice(0, 6) + '...' + linkedWallet.slice(-4);
      return `GRAM: ${short}`;
    }
    return t('wallet.connect_gram', 'Connect GRAM Wallet');
  })();

  // ── Progress bar ──────────────────────────────────────────────────────────
  // Пока есть несобранные награды — показываем самую старую из них на заполненной
  // шкале (max/max); иначе живой прогресс текущего этапа. Прогресс копится в фоне.
  const displayedStage  = selectDisplayedStageReward(progress);
  const clampedProgress = Math.max(0, Math.min(100, displayedStage.percentage ?? 0));
  const stageReward     = formatStageReward(displayedStage.reward);
  const stageRewardClaimable = displayedStage.claimable;

  // Клик по иконке награды: забрать награду за завершённый этап.
  // Разрешён и во время автоспина — начисление на бэке независимо от спина (обе операции
  // берут pessimistic lock на user_game_progress, поэтому просто сериализуются).
  const claimStageRewardInFlightRef = useRef(false);
  const handleClaimStageReward = async () => {
    if (!stageRewardClaimable || claimStageRewardInFlightRef.current) return;
    claimStageRewardInFlightRef.current = true;
    try {
      const result = await dispatch(claimProgressReward(displayedStage.claimId)).unwrap();
      const claimed = result?.claimedReward || displayedStage.reward;
      const fmt     = formatStageReward(claimed);
      const type    = claimed?.type || 'TICKETS';
      const label   = type === 'TON_COINS' ? 'GRAM' : type === 'COINS' ? 'Coins' : 'Ticket';
      dispatch(enqueueReward({
        type: type === 'TON_COINS' ? 'TON' : type, value: Number(claimed?.value) || 0, source: 'progress',
        icon: fmt.icon, label,
        animationTarget: type === 'TON_COINS' ? 'ton' : type === 'COINS' ? 'coins' : 'tickets',
        sound:           type === 'TON_COINS' ? 'gotTon' : type === 'COINS' ? 'gotCoins' : 'gotTicket',
      }));
      dispatch(fetchUser());
      dispatch(fetchWalletBalance());
      dispatch(fetchTicketStatus());
      dispatch(fetchNotifications());
    } catch (err) {
      console.error('Failed to claim progress reward:', err);
      // 422 = уже забрано/нечего забирать (например, двойной тап) — просто синхронизируем прогресс
      dispatch(fetchProgress());
    } finally {
      claimStageRewardInFlightRef.current = false;
    }
  };

  // ── onSpinEnd ─────────────────────────────────────────────────────────────
  const onSpinEnd = (index) => {
    if (rewardEnqueuedRef.current) return;
    if (finalSpinIndexRef.current === null || index !== finalSpinIndexRef.current) return;
    rewardEnqueuedRef.current = true;

    const r           = effectiveRewards[index];
    const moduleIcon  = getModuleImage(r, backendRewardDataRef.current);
    const lootboxIcon =
      r.raw.type === 'CHEST' ? (r.raw.chestImageUrl || r.icon) :
      r.raw.type === 'KEY'   ? (r.raw.keyImageUrl   || r.icon) :
      null;
    const rewardPayload = {
      type: r.raw.type, value: r.raw.value, source: 'spin',
      label: r.label, icon: lootboxIcon || moduleIcon,
      animationTarget: mapAnimation(r), sound: mapSound(r),
      moduleData: backendRewardDataRef.current?.module,
      chestTypeId:   r.raw.chestTypeId,
      chestRarity:   r.raw.chestRarity,
      chestImageUrl: r.raw.chestImageUrl,
      keyTypeId:     r.raw.keyTypeId,
      keyRarity:     r.raw.keyRarity,
      keyImageUrl:   r.raw.keyImageUrl,
    };

    if (autoSpinActiveRef.current) {
      // ── Autospin: accumulate reward, schedule next spin ──
      collectedRewardsRef.current.push(rewardPayload);

      setCollectedRewards(prev => {
        const existing = prev.find(i => i.label === r.label);
        if (existing) {
          return prev.map(i =>
            i.label === r.label
              ? { ...i, count: (Number(i.count) || 0) + (Number(r.raw.value) || 1) }
              : i
          );
        }
        return [...prev, { label: r.label, displayLabel: r.displayLabel, icon: moduleIcon, count: Number(r.raw.value) || 1 }];
      });

      nextSpinTimerRef.current = setTimeout(() => {
        nextSpinTimerRef.current = null;
        if (autoSpinActiveRef.current) {
          // Still in autospin — kick off the next spin
          spinInFlightRef.current    = false;
          rewardEnqueuedRef.current  = false;
          backendRewardDataRef.current = null;
          setBackendRewardData(null);
          finalSpinIndexRef.current  = null;
          handleSpinRef.current?.();
        }
        // If !autoSpinActiveRef here, handleStop already cancelled this timer
        // (or it fired after cancellation window) — nothing to do.
      }, 2000);

    } else if (stopRequestedRef.current) {
      // ── STOP was pressed while this spin was in flight ──
      // The reel just finished its last animation. Add the final reward to the
      // accumulated pool, then finalize after a brief settling delay so the
      // user sees the reel land before reward modals appear.
      collectedRewardsRef.current.push(rewardPayload);

      setCollectedRewards(prev => {
        const existing = prev.find(i => i.label === r.label);
        if (existing) {
          return prev.map(i =>
            i.label === r.label
              ? { ...i, count: (Number(i.count) || 0) + (Number(r.raw.value) || 1) }
              : i
          );
        }
        return [...prev, { label: r.label, displayLabel: r.displayLabel, icon: moduleIcon, count: Number(r.raw.value) || 1 }];
      });

      // Small pause so the user sees the reel settle before modals appear
      setTimeout(() => {
        finalizeStopAutoSpinRef.current?.();
      }, 400);

    } else {
      // ── Single spin: show reward modal, then refresh balance ──
      dispatch(enqueueReward(rewardPayload));

      // Balance update deferred until after the reward modal has appeared
      setTimeout(() => {
        dispatch(fetchWalletBalance());
        if (backendRewardDataRef.current?.type === 'MODULE') dispatch(fetchUser());
        // Spins unlock BattlePass tiers / achievements — refresh badges (BP pill, Tasks)
        dispatch(fetchNotifications());
        dispatch(fetchBattlePass());
        setSpin(false);
        spinInFlightRef.current = false;
      }, 1500);
    }
  };

  // Обратный отсчёт кулдауна рекламного тикета (HH:MM:SS, как в ячейке Shop)
  const adCooldownLeft = adTicketStatus && !adTicketStatus.isAvailable
    ? adFormatCountdown(adTicketStatus.nextAvailableTime)
    : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Server / network error banner ── */}
      {spinError && (
        <div
          onClick={() => setSpinError(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            background: '#c0392b', color: '#fff', padding: '12px 16px',
            textAlign: 'center', fontSize: '14px', cursor: 'pointer',
          }}
        >
          {spinError} &nbsp;✕
        </div>
      )}

      <div className="ls-page-wrapper">
        <SpinBackground
          ref={spinBgRef}
          isPlaying={spinning}
          smileVisible={!monitorReady}
          onCycleComplete={handleAnimationCycleComplete}
        />

        {/* ── Status row ── */}
        <div className="ls-status-row">
          {!boostEnabled && (
            <div className="ls-toggle-item" onClick={() => setShowBoostModal(true)}>
              <span className="ls-toggle-label">{t('spin.boost_your_luck', 'Boost your luck')}</span>
              <label className="ls-toggle-switch" onClick={e => e.preventDefault()}>
                <input type="checkbox" readOnly checked={false} onChange={() => {}} />
                <span className="ls-toggle-track" />
              </label>
            </div>
          )}

          {boostEnabled && (
            <div className="ls-ticket-count-row" onClick={() => setShowBoostModal(true)}>
              <img src="/tickets_icon.png" alt="ticket" className="ls-ticket-icon-sm" />
              <span className="ls-ticket-count-text">{t('spin.boost_count', '{{n}} boost', { n: boostTickets })}</span>
              <div className="ls-ticket-progress-bar">
                <div className="ls-ticket-progress-fill" style={{ width: `${Math.min(100, (boostTickets / 10) * 100)}%` }} />
              </div>
            </div>
          )}

          <div
            className={`ls-bp-pill${isDatumPassEnabled ? '' : ' ls-bp-pill--free'}`}
            onClick={() => navigate('/battle-pass')}
          >
            <img src={crownImg} alt="" className="ls-bp-pill-crown" />
            <span className="ls-bp-pill-season">
              {bpSeason?.name ? entityText(t, 'battle_pass_season', bpSeason.id, 'name', bpSeason.name) : t('spin.battle_pass', 'Battle Pass')}:
            </span>
            {isDatumPassEnabled ? (
              <>
                <img src="/clock.png" alt="" className="ls-clock-icon" />
                <span className="ls-bp-pill-time">
                  {bpSeason?.secondsLeft ? bpFormatCountdown(bpSeason.secondsLeft) : '—'}
                </span>
              </>
            ) : (
              <span className="ls-bp-pill-time">{t('spin.get_pass', 'Get Pass')}</span>
            )}
            {bpUnclaimedCount > 0 && (
              <span className="ls-bp-pill-badge">{bpUnclaimedCount}</span>
            )}
          </div>
        </div>

        <div className="ls-scroll-area">
          {/* Monitor area — invisible until first spin */}
          <div
            className="ls-monitor-area"
            style={{
              opacity: monitorReady ? 1 : 0,
              transition: 'opacity 0.6s ease',
              pointerEvents: monitorReady ? 'auto' : 'none',
            }}
          >
            <div className="ls-titles">
              <span className="ls-title">{t('spin.title', 'Lucky spin')}</span>
              <span className="ls-subtitle">{t('spin.subtitle', 'Collect all characters')}</span>
            </div>

            <div className="ls-slot-holder">
              <HorizontalSlotReel
                ref={slotRef}
                items={effectiveRewards}
                onSpinEnd={onSpinEnd}
              />
            </div>

            <div className="ls-total-spins" onClick={() => setShowPopover(!showPopover)}>
              <span>{t('spin.total_spins', 'Total Spins:')}</span>
              <img src="/loading_spinner_icon.png" alt="spins" className="ls-spin-icon" />
              <span className="ls-spins-count">{progress?.totalSpins ?? 0}</span>
              <Popover visible={showPopover} position="top" style={{ top: '430px', left: '50%', transform: 'translateX(-50%)' }}>
                <strong>{t('spin.popover.title', 'ROULETTE & AMOUNT OF SPINS')}</strong>
                <p>{t('spin.popover.text', 'Collect all the modules of characters')}</p>
              </Popover>
            </div>
          </div>

          {/* Progress bar. Когда есть награда для сбора — весь блок кликабелен,
              шкала подсвечена мигающей тенью снизу, иконка увеличена/светится/трясётся. */}
          <div
            className={`ls-progress-row${stageRewardClaimable ? ' ls-progress-row--claimable' : ''}${progress?.isClaiming ? ' ls-progress-row--claiming' : ''}`}
            role={stageRewardClaimable ? 'button' : undefined}
            tabIndex={stageRewardClaimable ? 0 : undefined}
            aria-label={stageRewardClaimable ? t('spin.claim_stage_aria', 'Claim {{amount}} {{type}}', { amount: stageReward.label, type: stageReward.type }) : undefined}
            onClick={stageRewardClaimable ? handleClaimStageReward : undefined}
            data-tutorial={stageRewardClaimable ? 'progress-claim' : undefined}
            onKeyDown={stageRewardClaimable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClaimStageReward(); } } : undefined}
          >
            <img src="/loading_spinner_icon.png" alt="progress" className="ls-progress-icon" />
            <div className="ls-progress-bar-wrap">
              <div className="ls-progress-bar-track">
                <div className="ls-progress-bar-fill" style={{ width: `${clampedProgress}%` }} />
              </div>
              <span className="ls-progress-text">
                {displayedStage.current}
                <span className="ls-progress-slash">/</span>
                {displayedStage.max}
              </span>
            </div>
            {/* Иконка награды: когда этап завершён — увеличена, плавает, светится и трясётся */}
            <div className={`ls-progress-ticket ls-progress-reward--${stageReward.type.toLowerCase()}${stageRewardClaimable ? ' ls-progress-ticket--claimable' : ''}`}>
              {stageRewardClaimable && <i className="ls-progress-ticket-glow" aria-hidden="true" />}
              <img src={stageReward.icon} alt={stageReward.type} />
              <span>{stageReward.label}</span>
              {progress?.pendingRewards?.length > 1 && (
                <span className="ls-progress-ticket-badge">×{progress.pendingRewards.length}</span>
              )}
            </div>
          </div>
        </div>

        {/* ── Bottom controls ── */}
        <div className="ls-bottom">
          {stoppingAutoSpin ? (
            /* ── STOPPING: last reel animation finishing after STOP press ── */
            <>
              <FreeTicketTimer />

              {/* Tapping this button recovers from a stuck "stopping" state */}
              <button
                className="app-btn app-btn--stop"
                onClick={() => finalizeStopAutoSpinRef.current?.()}
              >
                <span className="ls-stop-dots">• • •</span>
              </button>

              <div className="ls-btn-autospin" style={{ opacity: 1 }}>{t('spin.getting_rewards', 'Getting your rewards...')}</div>

              {/* Keep showing accumulated rewards so user sees what's coming */}
              <div className="ls-rewards-collected">
                {collectedRewards.map((item, idx) => (
                  <div key={idx} className="ls-reward-item">
                    <img src={item.icon} alt={item.displayLabel || item.label} />
                    <span>
                      {typeof item.count === 'number'
                        ? (Number.isInteger(item.count) ? item.count : item.count.toFixed(2))
                        : item.count}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : !autoSpinMode ? (
            /* ── IDLE / SINGLE SPIN STATE ── */
            <>
              <FreeTicketTimer />

              {spinning ? (
                <button className="app-btn app-btn--stop" disabled>
                  <span className="ls-stop-dots">• • •</span>
                </button>
              ) : isAvailable ? (
                <button
                  className="app-btn app-btn--yellow"
                  data-tutorial="spin-button"
                  onClick={handleClaimOnly}
                  disabled={isClaiming}
                >
                  {t('spin.claim_free_ticket', 'Claim free ticket')}
                  <img src="/ticket_icon.png" alt="ticket" className="ls-btn-ticket-icon" />
                  <span>{pendingTickets}</span>
                </button>
              ) : (boostTickets > 0 || totalTicketsClaimed > 0) ? (
                <button
                  className="app-btn app-btn--green"
                  data-tutorial="spin-button"
                  onPointerDown={handlePressStart}
                  onPointerUp={handlePressEnd}
                  onPointerLeave={handlePressCancel}
                >
                  {t('spin.start', 'Start')}
                  <img
                    src={boostTickets > 0 ? '/tickets_icon.png' : '/ticket_icon.png'}
                    alt="ticket"
                    className="ls-btn-ticket-icon"
                  />
                  <span>{boostTickets > 0 ? boostTickets : totalTicketsClaimed}</span>
                </button>
              ) : (
                <div className="ls-no-tickets-row">
                  {/* 0 тикетов и бесплатный тикет недоступен: рекламная кнопка здесь
                      всегда (как ячейка в Shop) — активная, когда кулдаун прошёл,
                      с countdown, когда нет. «Please wait» остаётся только если
                      статус ещё не загружен или у AdsGram нет рекламы (403). */}
                  {adUnavailable || !adTicketStatus ? (
                    <button
                      className="app-btn app-btn--wait"
                      onClick={() => spinBgRef.current?.playNoTickets()}
                    >
                      {t('spin.please_wait', 'Please wait & get free tickets')}
                    </button>
                  ) : adTicketStatus.isAvailable ? (
                    <button
                      className="app-btn app-btn--blue ls-adv-btn"
                      onClick={handleWatchAdv}
                      disabled={isClaiming}
                    >
                      <img src="/adv_icon.svg" alt="" className="ls-adv-icon" />
                      {isClaiming ? t('ads.loading', 'Loading ad…') : t('ads.watch_for_ticket', 'Watch ADV to get free ticket')}
                    </button>
                  ) : (
                    <button className="app-btn app-btn--blue ls-adv-btn" disabled>
                      <img src="/adv_icon.svg" alt="" className="ls-adv-icon" />
                      {adCooldownLeft
                        ? t('ads.watch_in', 'Watch ADV in {{time}}', { time: adCooldownLeft })
                        : t('ads.watch_for_ticket', 'Watch ADV to get free ticket')}
                    </button>
                  )}
                  {datumCoins > 0 && (
                    <button
                      className="app-btn app-btn--purple ls-convert-btn"
                      onClick={() => setShowConverter(true)}
                      aria-label={t('spin.convert_coins', 'Convert coins to tickets')}
                      title={t('spin.convert_coins', 'Convert coins to tickets')}
                    >
                      <img src="/coin_icon.png" alt="" className="ls-convert-icon" />
                      <span className="ls-convert-arrow">→</span>
                      <img src="/ticket_icon.png" alt="" className="ls-convert-icon" />
                    </button>
                  )}
                </div>
              )}

              {/* Invisible — keeps layout height stable, no hint visible during spin */}
              <span className="ls-btn-autospin" style={{ opacity: (spinning || totalTicketsClaimed <= 1)?0:1 }}>
                {t('spin.hold_autospin', 'Hold For AutoSpin')}
              </span>

              {!isWalletLinked && (
                <button
                  className="app-btn app-btn--purple"
                  onClick={handleConnectTon}
                  disabled={tonConnecting}
                >
                  {tonButtonLabel}
                </button>
              )}

              {isWalletLinked && (
                <button
                  className="app-btn app-btn--purple"
                  onClick={() => setShowInviteFriend(true)}
                >
                  {t('friends.invite_button', 'Invite a friend')}
                </button>
              )}
            </>
          ) : (
            /* ── AUTOSPIN STATE ── */
            <>
              <FreeTicketTimer />

              <button className="app-btn app-btn--stop" onPointerDown={handleStop}>
                <span className="ls-stop-dots">• • •</span>
                <span className="ls-stop-label">{t('spin.stop', 'STOP')}</span>
                <span className="ls-stop-dots">• • •</span>
              </button>

              <span className="ls-stop-hint">
                {t('spin.stop_hint', 'Press "STOP" to cancel AutoSpin & get rewards')}
              </span>

              {/* Accumulated rewards row */}
              <div className="ls-rewards-collected">
                {collectedRewards.map((item, idx) => (
                  <div key={idx} className="ls-reward-item">
                    <img src={item.icon} alt={item.displayLabel || item.label} />
                    <span>
                      {typeof item.count === 'number'
                        ? (Number.isInteger(item.count) ? item.count : item.count.toFixed(2))
                        : item.count}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showInviteFriend && <InviteFriendsModal onClose={() => setShowInviteFriend(false)} />}
      {showTicketModal && (
        <TicketCollectorModal onClose={() => { setShowTicketModal(false); refreshUser(); }} />
      )}
      {showConverter && (
        <TicketConverterModal
          onClose={() => { setShowConverter(false); dispatch(fetchWalletBalance()); }}
        />
      )}
      {showBoostModal && (
        <BoostModal
          onClose={() => { setShowBoostModal(false); dispatch(fetchTicketStatus()); }}
          item={boostTicketItem}
        />
      )}
      {showAutoCollectModal && (
        <AutoCollectModal
          onClose={() => setShowAutoCollectModal(false)}
          items={autoCollectItems}
          item={autoCollectItems[0]}
        />
      )}
      {showDatumPassModal && (
        <DatumPassOfferModal onClose={() => setShowDatumPassModal(false)} />
      )}
      {showDatumSpinModal && (
        <DatumSpinModal onClose={() => setShowDatumSpinModal(false)} />
      )}
    </>
  );
}

/** HH:MM:SS до конца кулдауна рекламного тикета (null, когда кулдаун прошёл) */
function adFormatCountdown(nextAvailableTime) {
  if (!nextAvailableTime) return null;
  const diff = new Date(nextAvailableTime).getTime() - Date.now();
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1_000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function bpFormatCountdown(seconds) {
  if (!seconds || seconds <= 0) return '0d 0h';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return `${days}d ${hours}h`;
}
